import { useEffect, useState } from "react";
import CRM_API from "../../api/crmApi";

const tempColor = { hot: "#ef4444", warm: "#f59e0b", cold: "#06b6d4" };
const tempBg = { hot: "rgba(239,68,68,.1)", warm: "rgba(245,158,11,.1)", cold: "rgba(6,182,212,.1)" };

export default function CrmCallingPanel({ onNavigate }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchLeads = async () => {
    try {
      const res = await CRM_API.get("/leads");
      setLeads(res.data);
    } catch (error) {
      console.log("Error loading leads", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px" }}>
        <div className="spinner-border text-primary me-2" role="status"></div>
        <span style={{ color: "#6b7280" }}>Loading leads...</span>
      </div>
    );
  }

  const filteredLeads = leads.filter(l =>
    l.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.student_contact?.includes(search) ||
    l.interested_course?.toLowerCase().includes(search.toLowerCase())
  );

  // Separate hot leads for priority display
  const hotLeads = filteredLeads.filter(l => l.temperature === "hot");
  const otherLeads = filteredLeads.filter(l => l.temperature !== "hot");
  const orderedLeads = [...hotLeads, ...otherLeads];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ margin: 0, fontWeight: 800, color: "#111827", fontSize: "22px" }}>Calling Panel</h4>
        <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#9ca3af" }}>
          {leads.length} leads &mdash; {hotLeads.length} hot priority
        </p>
      </div>

      {/* Quick stats */}
      <div className="row g-3 mb-4">
        {[
          { label: "Total", count: leads.length, color: "#2b27ea", bg: "rgba(43,39,234,.08)", icon: "bi-people-fill" },
          { label: "Hot 🔥", count: leads.filter(l => l.temperature === "hot").length, color: "#ef4444", bg: "rgba(239,68,68,.08)", icon: "bi-fire" },
          { label: "Warm 🟡", count: leads.filter(l => l.temperature === "warm").length, color: "#f59e0b", bg: "rgba(245,158,11,.08)", icon: "bi-thermometer-half" },
          { label: "Cold 🔵", count: leads.filter(l => l.temperature === "cold").length, color: "#06b6d4", bg: "rgba(6,182,212,.08)", icon: "bi-thermometer-low" },
        ].map(s => (
          <div className="col-3" key={s.label}>
            <div style={{ background: "#fff", borderRadius: "12px", padding: "14px 18px", boxShadow: "0 2px 10px rgba(0,0,0,.05)", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: 38, height: 38, borderRadius: "10px", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: "16px" }}></i>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: "11px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</p>
                <h5 style={{ margin: 0, fontWeight: 800, color: s.color, fontSize: "20px" }}>{s.count}</h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: "relative", maxWidth: "340px", marginBottom: "20px" }}>
        <i className="bi bi-search" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }}></i>
        <input
          className="form-control"
          placeholder="Search name / mobile / course"
          style={{ paddingLeft: "38px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "14px" }}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "16px", boxShadow: "0 2px 14px rgba(0,0,0,.06)", overflow: "hidden", border: "1px solid rgba(0,0,0,.04)" }}>
        <div style={{ background: "linear-gradient(135deg,#16a34a,#22c55e)", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h6 style={{ margin: 0, color: "#fff", fontWeight: 700, fontSize: "14px" }}>
            <i className="bi bi-telephone-fill me-2"></i>Call Queue
          </h6>
          <span style={{ color: "rgba(255,255,255,.8)", fontSize: "12px" }}>Hot leads first</span>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
            <thead style={{ background: "#f8faff" }}>
              <tr>
                {["#", "Name", "Mobile", "Course", "Status", "Temperature", "Next Followup", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", fontWeight: 700, color: "#6b7280", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedLeads.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                    <i className="bi bi-telephone-x" style={{ fontSize: "32px", display: "block", marginBottom: "8px" }}></i>
                    No leads available
                  </td>
                </tr>
              ) : (
                orderedLeads.map((lead, idx) => (
                  <tr key={lead.id} style={{ background: lead.temperature === "hot" ? "rgba(239,68,68,.02)" : "" }}>
                    <td style={{ padding: "12px 16px", color: "#9ca3af", fontSize: "12px" }}>{idx + 1}</td>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: "#111827" }}>
                      {lead.temperature === "hot" && <i className="bi bi-fire text-danger me-1" style={{ fontSize: "12px" }}></i>}
                      {lead.student_name}
                    </td>
                    <td style={{ padding: "12px 16px", color: "#374151", fontWeight: 600 }}>{lead.student_contact}</td>
                    <td style={{ padding: "12px 16px", color: "#374151", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.interested_course}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: "rgba(43,39,234,.1)", color: "#2b27ea", borderRadius: "6px", padding: "3px 10px", fontSize: "12px", fontWeight: 600 }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{
                        background: tempBg[lead.temperature] || "rgba(0,0,0,.05)",
                        color: tempColor[lead.temperature] || "#6b7280",
                        borderRadius: "6px", padding: "3px 10px", fontSize: "12px", fontWeight: 600,
                      }}>
                        {lead.temperature}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#6b7280", fontSize: "12px" }}>
                      {lead.next_followup
                        ? <><i className="bi bi-calendar3 me-1"></i>{lead.next_followup}</>
                        : <span style={{ color: "#d1d5db" }}>—</span>
                      }
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <a
                          href={`tel:${lead.student_contact}`}
                          style={{ background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "#fff", border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "5px", boxShadow: "0 2px 8px rgba(22,163,74,.3)" }}
                        >
                          <i className="bi bi-telephone-fill"></i> Call
                        </a>
                        <button
                          onClick={() => onNavigate("crm-lead-profile", lead.id)}
                          style={{ background: "linear-gradient(135deg,#2b27ea,#069ee6)", color: "#fff", border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px", boxShadow: "0 2px 8px rgba(43,39,234,.3)" }}
                        >
                          <i className="bi bi-person-fill"></i> Profile
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
